/**
 * GoogleMapsJsMapAdapter — the OD-009 Google Maps adapter for Admin Web
 * (ODE-P07-T02, REQ-084).
 *
 * Runtime-gated: this module is only ever CONSTRUCTED when a Google Maps
 * API key is present (registry in `map-provider.ts`); it is never imported
 * eagerly and the key is never committed. The Maps JavaScript API script is
 * loaded lazily at mount time through an injectable loader seam
 * (`GoogleMapsLoader`), so tests can exercise the adapter contract without
 * any external map network.
 *
 * Fail-safe behavior: a loader failure or mount error is surfaced through
 * `onError` — the surrounding UI renders the REQ-090 error/misconfigured
 * state. The adapter never throws asynchronously into React and never
 * fabricates positions: markers are set exactly as passed in.
 *
 * Traces to: ODE-PRD-REQ-084, ODE-PRD-REQ-090, ODE-OD-009.
 */

import type { MapMarkerView, MapProvider } from './map-provider.js';

/** Minimal structural surface of the Google Maps JS API this adapter uses. */
export interface GoogleMapLike {
  setCenter(point: { lat: number; lng: number }): void;
  setZoom(zoom: number): void;
}

export interface GoogleMapsApiLike {
  Map: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMapLike;
  Marker: new (options: Record<string, unknown>) => { setMap(map: unknown | null): void };
}

/** Loader seam: resolves the Google Maps JS API for a given key. */
export type GoogleMapsLoader = (apiKey: string) => Promise<GoogleMapsApiLike>;

let loadPromise: Promise<GoogleMapsApiLike> | null = null;
let loadedKey: string | null = null;

/**
 * Default production loader: injects the Maps JavaScript API `<script>`
 * once per key and resolves with the `google.maps` namespace. Never used in
 * tests (fake adapter) and only reachable when a runtime key exists.
 */
export const loadGoogleMapsJs: GoogleMapsLoader = (apiKey: string): Promise<GoogleMapsApiLike> => {
  if (loadPromise !== null && loadedKey === apiKey) return loadPromise;
  loadedKey = apiKey;
  loadPromise = new Promise<GoogleMapsApiLike>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Google Maps JS cannot load outside a browser context'));
      return;
    }
    const existing = (globalThis as { google?: { maps?: GoogleMapsApiLike } }).google;
    if (existing?.maps !== undefined) {
      resolve(existing.maps);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.onload = () => {
      const maps = (globalThis as { google?: { maps?: GoogleMapsApiLike } }).google?.maps;
      if (maps === undefined) {
        reject(new Error('Google Maps JS loaded but google.maps is unavailable'));
        return;
      }
      resolve(maps);
    };
    script.onerror = () => reject(new Error('Google Maps JS failed to load'));
    document.head.appendChild(script);
  });
  return loadPromise;
};

export interface GoogleMapsJsMapAdapterOptions {
  /** Loader seam (default: the real Maps JS script loader). */
  readonly loader?: GoogleMapsLoader;
  /** Called on any adapter failure (UI renders the REQ-090 error state). */
  readonly onError?: (message: string) => void;
}

export class GoogleMapsJsMapAdapter implements MapProvider {
  readonly name = 'google';
  private readonly loader: GoogleMapsLoader;
  private readonly onError: (message: string) => void;
  private map: GoogleMapLike | null = null;
  private container: HTMLElement | null = null;
  private markers: readonly MapMarkerView[] = [];
  private markerObjects: Array<{ setMap(map: unknown | null): void }> = [];
  private destroyed = false;
  private api: GoogleMapsApiLike | null = null;

  constructor(
    private readonly apiKey: string,
    options: GoogleMapsJsMapAdapterOptions = {},
  ) {
    this.loader = options.loader ?? loadGoogleMapsJs;
    this.onError =
      options.onError ??
      (() => {
        /* default: failures are surfaced through onError only when provided */
      });
  }

  mount(container: HTMLElement): void {
    if (this.destroyed) return;
    this.container = container;
    void this.loader(this.apiKey)
      .then((api) => {
        if (this.destroyed || this.container !== container) return;
        this.api = api;
        this.map = new api.Map(container, {
          center: { lat: -6.2, lng: 106.816666 }, // Jakarta default view
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
        });
        this.renderMarkers();
      })
      .catch((error: unknown) => {
        if (this.destroyed) return;
        this.onError(error instanceof Error ? error.message : 'Gagal memuat Google Maps.');
      });
  }

  setMarkers(markers: readonly MapMarkerView[]): void {
    if (this.destroyed) return;
    this.markers = markers.map((marker) => ({ ...marker }));
    this.renderMarkers();
  }

  private renderMarkers(): void {
    if (this.map === null || this.api === null) return;
    // Clear previous overlay objects (idempotent marker rendering).
    for (const marker of this.markerObjects) {
      marker.setMap(null);
    }
    this.markerObjects = [];
    for (const marker of this.markers) {
      const overlay = new this.api.Marker({
        position: { lat: marker.latitude, lng: marker.longitude },
        map: this.map,
        title: marker.label,
      });
      this.markerObjects.push(overlay);
    }
  }

  destroy(): void {
    this.destroyed = true;
    for (const marker of this.markerObjects) {
      try {
        marker.setMap(null);
      } catch {
        // A marker teardown failure never prevents map teardown.
      }
    }
    this.markerObjects = [];
    this.map = null;
    this.container = null;
    this.markers = [];
  }
}
